<template>
	<div class="min-h-screen bg-gray-100">
		<div class="max-w-3xl mx-auto bg-white shadow-md rounded-lg p-6 sm:p-8">
			<h1 class="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-6">{{ APP_DISPLAY_NAME }}个人信息保护政策</h1>
			<p class="text-gray-600 mb-6">
				更新日期：2026年1月20日
				<br />
				生效日期：2026年1月20日
			</p>

			<!-- 引言 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">引言</h2>
				<p class="text-gray-700 leading-relaxed mb-4">
					{{ APP_DISPLAY_NAME }}个人信息保护政策（下称"本政策"）是用户与{{ COMPANY_NAME }}{{ APP_DISPLAY_NAME }}APP（下称"{{ APP_DISPLAY_NAME }}"）之间的协议。在{{ APP_DISPLAY_NAME }}平台浏览、创建账户并使用之前，用户必须首先同意本条款。用户浏览、创建账户后即视为接受本条款的全部内容。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">{{ APP_DISPLAY_NAME }}有权在必要时对本条款进行修订，且毋须另行通知用户。修订后的条款一旦在网页上公布即有效代替原来的服务条款。</p>
				<p class="text-gray-700 leading-relaxed mb-4">
					我们根据《中华人民共和国网络安全法》等相关法律法规制定《{{ APP_DISPLAY_NAME }}个人信息保护政策》（以下简称"本政策"）并保护您的个人信息安全。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">
					所有{{ APP_DISPLAY_NAME }}应用产品或服务均适用本政策。本政策介绍了我们在向您提供{{ APP_DISPLAY_NAME }}应用产品或服务的过程中收集、使用、存储、共享、转让、公开披露您个人信息的目的、方式、范围和信息安全保护措施，以及您管理个人信息的权利和实现方法。请您结合我们的服务条款仔细阅读本政策，并在同意本政策约定内容的情况下使用我们的产品或服务。对所有重点内容，我们采用"
					<strong>加粗</strong>
					"的书写方式进行提示。
				</p>
			</section>

			<!-- 关键词定义 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">关键词定义</h2>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<strong>{{ APP_DISPLAY_NAME }}平台</strong>
						：指由{{ COMPANY_NAME }}开发的平台，包括但不限于官方网站、app、小程序、客户端及其各自版本。
					</li>
					<li>
						<strong>{{ APP_DISPLAY_NAME }}产品或服务</strong>
						：指{{ APP_DISPLAY_NAME }}向您提供的以下产品或服务：
						<ul class="list-decimal pl-6 mt-2">
							<li>通过{{ APP_DISPLAY_NAME }}应用提供的{{ APP_DISPLAY_NAME }}产品或服务；</li>
							<li>与上述产品或服务相关的任何软件；</li>
							<li>我们不时为您提供的其他产品或服务。</li>
						</ul>
					</li>
					<li>
						<strong>用户或您</strong>
						：指在{{ APP_DISPLAY_NAME }}官网
						<span class="text-blue-600 hover:underline">{{ OFFICIAL_WEBSITE }}</span>
						{{ APP_DISPLAY_NAME }}APP（iOS/Android）上浏览、创建账户并使用及保管该账户的自然人、法人、其他组织。
					</li>
					<li>
						<strong>未成年人</strong>
						：指年龄不超过18周岁的未成年人。
					</li>
					<li>
						<strong>儿童</strong>
						：指未满14周岁的未成年人。
					</li>
					<li>
						<strong>个人信息</strong>
						：指以电子或者其他方式记录的、能够单独或者与其他信息结合识别特定自然人身份或者反映特定自然人活动情况的各种信息。个人信息包括个人基本信息、个人身份信息、个人生物识别信息、网络身份标识信息、个人健康生理信息、个人教育工作信息、个人财产信息、个人通信信息、联系人信息、个人上网记录、个人常用设备信息、个人位置信息等。为免疑义，个人信息包括但不限于个人敏感信息。
					</li>
					<li>
						<strong>个人敏感信息</strong>
						：指一旦泄露、非法提供或滥用可能危害人身和财产安全，极易导致个人名誉、身心健康受到损害或歧视性待遇等的个人信息。个人敏感信息包括
						<strong><em>个人财产信息、个人健康生理信息、个人生物识别信息、个人身份信息</em></strong>
						等。
					</li>
					<li>
						<strong>通讯录信息</strong>
						：本政策下所称通讯录信息仅包括通讯录中联系人的名称信息，如姓名、昵称等。
					</li>
					<li>
						<strong>去标识化</strong>
						：指通过对个人信息的技术处理，使其在不借助额外信息的情况下，无法识别您的过程。
					</li>
					<li>
						<strong>匿名化</strong>
						：指通过对个人信息的技术处理，使得您无法被识别，且处理后的信息不能被复原的过程。
					</li>
					<li>
						<strong>中国或中国境内</strong>
						：指中华人民共和国大陆地区（仅为本政策之目的，不包含香港特别行政区、澳门特别行政区和台湾地区）。
					</li>
					<li>
						<strong>设备</strong>
						：指可用于访问{{ APP_DISPLAY_NAME }}产品或服务的装置。
					</li>
				</ul>
			</section>

			<!-- 特别提示 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">特别提示</h2>
				<p class="text-gray-700 leading-relaxed mb-4">以下是我们的特别提示，请您重点关注：</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						对
						<strong>个人敏感信息</strong>
						，我们用"
						<strong><em>加粗斜体</em></strong>
						"的书写方式进行特别提醒，希望您在阅读时特别关注。在您向我们提供您的任何
						<strong><em>个人敏感信息</em></strong>
						前，您已慎重考虑并在此确认该等提供是适当的，您在此同意您的
						<strong><em>个人敏感信息</em></strong>
						可按照本政策所述的目的和方式进行处理。
					</li>
					<li>我们的个人信息权利实现方式包括但不限于访问、更正、删除、注销、撤回授权、及时得到响应等，以确保您实现对您个人信息的控制权。</li>
					<li>我们会严格依据本政策以及您的同意处理您的个人信息。如您不同意本政策，您应当停止使用{{ APP_DISPLAY_NAME }}应用产品或服务。</li>
				</ul>
			</section>

			<!-- 1. 如何收集和使用您的个人信息 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">1. 如何收集和使用您的个人信息</h2>
				<p class="text-gray-700 leading-relaxed mb-4">
					我们会根据合法正当、必要、透明的原则，基于本政策所述的目的，收集和使用您的个人信息。如果我们将您的个人信息用于本政策未载明的其它用途，或基于其他特定目的而收集您的个人信息，我们将以合理的方式告知您，并在使用前再次征得您的同意。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">1.1 向您提供产品或服务过程中您主动提供的个人信息</h3>

				<h4 class="text-base font-medium text-gray-800 mb-2">1.1.1 账号注册与登录</h4>
				<p class="text-gray-700 leading-relaxed mb-4">您可以通过以下方式注册和/或登录{{ APP_DISPLAY_NAME }}平台，包括但不限于：</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<strong>手机号注册/登录</strong>
						：当您通过手机号方式注册和/或登录{{ APP_DISPLAY_NAME }}账号时应用，您需要向我们提供您的
						<strong><em>手机号码</em></strong>
						。我们会向您的手机号码发送短信验证码，您需要输入短信验证码以验证您的身份后方可成功注册和/或登录{{ APP_DISPLAY_NAME }}账号。根据我国的法律法规及出于安全保障的考虑，如您不提供手机号码进行核验，将导致注册失败，进而无法使用相关功能。
					</li>
					<li>
						<strong>电子邮箱注册/登录</strong>
						：当您通过电子邮箱的方式注册和/或登录{{ APP_DISPLAY_NAME }}账号时，您需要向我们提供您的
						<strong><em>电子邮箱地址</em></strong>
						以及您的密码以完成注册和/或登录。
					</li>
					<li>
						<strong>第三方跳转注册/登录</strong>
						：具体信息，请详见下文1.4.1节的内容。
					</li>
					<li>如您不提供上述注册登录方式所需的信息，则无法登录{{ APP_DISPLAY_NAME }}应用账号，同时也会影响您使用{{ APP_DISPLAY_NAME }}应用的所有功能。</li>
				</ul>

				<h4 class="text-base font-medium text-gray-800 mb-2 mt-4">1.1.2 为了向您提供更优质的{{ APP_DISPLAY_NAME }}产品和/或服务</h4>
				<p class="text-gray-700 leading-relaxed mb-4">
					我们可能需要收集下述信息。如果您拒绝提供下述信息，不影响您正常使用 1.1项下基本业务功能，但我们将无法向您提供相对应的扩展业务功能和服务。
				</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<strong>完成支付结算</strong>
						：您选择付费购买{{ APP_DISPLAY_NAME }}产品和/或服务（包括但不限于购买素材、模板等）时，可以选择使用微信或支付宝进行支付。您在使用支付服务时，{{ APP_DISPLAY_NAME }}会收集您的
						<strong><em>账单信息</em></strong>
						及其他法律要求的信息并与第三方支付平台共享，以确认您的支付指令并帮助您能完成支付。对于您的支付行为，{{ APP_DISPLAY_NAME }}不直接收集您的任何
						<strong><em>银行账户信息</em></strong>
						。
					</li>
					<li>
						<strong>客服或投诉</strong>
						：您可以在{{ APP_DISPLAY_NAME }}平台内或通过本政策第8条提供的方式向我们提出意见反馈、投诉、申诉或咨询本产品的相关问题。为了您的账号与系统安全，我们会对您的用户身份进行核验。如果您选择以在线留言的方式与我们联系，您需要向我们提供相应的您的
						<strong>
							姓名（选填）、
							<em>邮箱（必填）</em>
							、手机号（选填）
						</strong>
						以及您主动输入的问题情况，以便与您联系并帮助您解决问题，或记录相关问题的处理方案及结果。此外，系统会记录您与客服之间的沟通记录，以便能够更好地帮您解决所咨询的问题和改善客服质量。
					</li>
					<li>
						<strong>复制ID或网址等</strong>
						：功能当您在使用{{ APP_DISPLAY_NAME }}过程中选择复制按钮（如复制订单编号、用户 ID等特定标识信息）时，为实现快捷复制功能，我们的软件会将对应的
						ID内容临时写入系统剪切板。该过程中我们仅收集您主动触发复制操作的
						ID信息，且不会读取或存储剪切板中的其他内容。此功能依赖系统剪切板权限实现，若您拒绝授权剪切板功能，可能导致无法使用复制
						ID的快捷操作，但不影响其他基础功能的正常使用。我们严格遵循最小必要原则，仅在您主动操作时临时使用剪切板，确保您的信息安全与隐私保护。
					</li>
				</ul>

				<h3 class="text-lg font-medium text-gray-800 mb-2 mt-4">1.2 向您提供产品或服务过程中我们主动收集的个人信息</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					在符合法律法规的前提下，为了保障您的账号安全以及为您提供更优质的服务，我们会收集和使用您在使用{{ APP_DISPLAY_NAME }}产品或服务过程中产生的如下信息：
				</p>

				<h4 class="text-base font-medium text-gray-800 mb-2">1.2.1 设备信息</h4>
				<p class="text-gray-700 leading-relaxed mb-4">
					为了使{{ APP_DISPLAY_NAME }}应用与设备进行必要的适配并安全服务，在您使用{{ APP_DISPLAY_NAME }}应用时，根据您在软件安装及使用中授予的具体权限，我们会收集（包括后台运行状态下收集）您的
					<strong>
						<em>
							设备型号、设备识别码（安卓ID、Android
							ID、iOS广告标识符（IDFA）（仅针对iOS13及以下版本收集）、IMEI、OAID、IMSI、Mac地址、UUID、WI-FI的SSID（服务集标识）、已安装包名列表、系统版本
						</em>
					</strong>
					。收集这些信息是为了帮助我们进行bug分析，保障您正常使用{{ APP_DISPLAY_NAME }}应用的服务、改进和优化我们的产品体验、保障您的账号安全。但我们无法保证其他第三方不会通过其他途径获知您设备信息、并将设备信息与其他信息相结合识别您个人身份的情况，由此造成您任何损失的，您应向该第三方追偿。上述设备信息是为提供服务所收集的基础信息，如您不想设备信息被我们收集，则可能导致我们无法向您提供正常的服务。
				</p>

				<h4 class="text-base font-medium text-gray-800 mb-2">1.2.2 日志信息</h4>
				<p class="text-gray-700 leading-relaxed mb-4">
					当您使用{{ APP_DISPLAY_NAME }}产品或服务时，我们会收集（包括后台运行状态下收集）您的相关网络日志信息，包括
					<strong><em>网络崩溃、系统活动信息、浏览记录、点击记录、软件使用记录、操作系统版本、浏览器类型、IP地址、退出页面和URL信息</em></strong>
					，以便于我们改善产品或服务的相关功能，为您提供更好的服务体验。
				</p>

				<h4 class="text-base font-medium text-gray-800 mb-2">1.2.3 交易记录</h4>
				<p class="text-gray-700 leading-relaxed mb-4">
					当您购买{{ APP_DISPLAY_NAME }}产品或服务时，我们会收集您的
					<strong><em>交易记录</em></strong>
					，以便完成{{ APP_DISPLAY_NAME }}模板或素材的购买流程。如您不同意我们收集上述信息，可能会导致您相应的购买操作不成功，无法使用该付费模板或素材。当您使用免费模板及素材时，您无需向我们提供额外的信息。
				</p>
				<p class="text-gray-700 leading-relaxed">
					此外，为了提高您使用{{ APP_DISPLAY_NAME }}服务的安全性，保护您或其他用户或公众的人身财产权免遭侵害，更好地预防钓鱼网站、网络漏洞、网络欺诈、网络攻击等安全风险，我们可能会使用您的
					<strong><em>注册信息、交易信息、设备信息和服务日志信息</em></strong>
					以及我们从合作第三方获取的您的信息来综合监控、判断交易风险、进行身份验证并采取必要的记录和处置措施。
				</p>

				<h4 class="text-base font-medium text-gray-800 mb-2 mt-4">1.2.4 静默状态与后台运行时个人信息收集规则</h4>
				<p class="text-gray-700 leading-relaxed mb-4">本应用在用户未主动使用应用、应用处于后台运行、设备处于锁屏静默状态时，可能会收集以下个人信息：</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2 mb-4">
					<li>
						<strong>收集信息类型</strong>
						：设备信息、网络状态、位置信息（如开启相关权限）、应用运行日志、崩溃日志。
					</li>
					<li>
						<strong>收集目的</strong>
						：保障服务稳定运行、消息推送、安全风控、异常排查、功能后台持续服务。
					</li>
					<li>
						<strong>收集方式</strong>
						：自动收集、SDK 采集、后台周期性上报。
					</li>
					<li>
						<strong>收集范围</strong>
						：仅收集实现功能所必需的最小信息，不收集与服务无关的个人信息。
					</li>
					<li>
						<strong>用户权利</strong>
						：用户可在系统设置中关闭后台活动、关闭相关权限，拒绝后台收集，不影响核心功能使用。
					</li>
				</ul>
				<p class="text-gray-700 leading-relaxed mb-4">
					<strong>重要提示：</strong>
					您知晓并同意，本应用在后台运行、静默状态、锁屏状态下，为实现上述目的收集个人信息。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2 mt-4">1.3 {{ APP_DISPLAY_NAME }}应用所调用的系统权限</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					如上所述，为了向您提供{{ APP_DISPLAY_NAME }}产品或服务，我们可能需要使用您设备中的系统权限（不包括所接入第三方调用的权限）来收集相关个人信息。尽管我们在前两节中已经有所涉及，本节将对{{ APP_DISPLAY_NAME }}所使用的所有系统权限进行归纳整理，以方便向您更加系统、完整、透明地呈现。您可以选择是否授权我们使用您设备的相关权限，如果您不授权我们使用相关权限，可能会导致部分{{ APP_DISPLAY_NAME }}产品或服务无法使用，但是不影响您使用{{ APP_DISPLAY_NAME }}应用的其他功能。
				</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<strong>摄像头权限</strong>
						：当您同意开启此项权限后，我们会使用您设备上的摄像头功能。此项权限将用于实现上传头像、素材的照片/图片摄录功能，并采集上述功能实现过程中所摄录的
						<strong><em>照片/图片信息</em></strong>
						。如果您拒绝该项授权，前述功能将无法使用。
					</li>
					<li>
						<strong>麦克风权限</strong>
						：当您同意开启此项权限后，我们会使用您设备上的麦克风功能。此项权限将用于实现素材制作过程中的音频录制功能。如果您拒绝该项授权，前述功能将无法使用。
					</li>
					<li>
						<strong>相册权限</strong>
						：当您同意开启此项权限后，我们会使用您设备上的相册的读取和存储功能。此项权限将用于实现上传头像、素材制作中的照片上传功能；您也可以将素材制作后的图片下载并存储在您手机的相册中。在此过程中我们会采集实现上述功能所需的
						<strong><em>图片信息</em></strong>
						。我们不会主动获取相册中的照片，仅在您同意开启此权限并在相册中主动选择相应图片后才会获取图片数据。如果您拒绝该项授权，前述功能将无法使用。
					</li>
					<li>
						<strong>iOS设备调用的其他权限</strong>
						：
						<ul class="list-decimal pl-6 mt-2">
							<li>
								<strong>IDFA</strong>
								：如果您为iOS版本13及以下的用户，我们会在App首次运行时征得您关于此项权限的同意，以用于保障您的账号、交易安全和以及为您提供服务。如果您拒绝该项授权，前述功能将无法使用。
							</li>
							<li>
								<strong>Siri</strong>
								：当您使用iOS设备登录我们的产品并使用捷径功能时，我们可能会获取您设备的Siri使用权限，并收集您的
								<strong><em>语音信息、语音交互信息</em></strong>
								。如您拒绝提供仅会使您无法使用该功能，但并不影响您正常使用产品或服务的其他功能。您可以在您的iOS设备上的设置功能中找到{{ APP_DISPLAY_NAME }}app，并选择关闭快捷指令。当您取消该授权后，我们将不再收集该信息，也无法再为您提供上述与之对应的服务。
							</li>
						</ul>
					</li>
					<li>
						<strong>安卓设备调用的其他权限</strong>
						：
						<ul class="list-decimal pl-6 mt-2">
							<li>
								<strong>读取应用程序列表</strong>
								：我们会在安卓App运行时使用您设备上的该项权限，用于防止被其他恶意仿冒程序实施界面劫持攻击的风控评估。该项权限为安卓端App确保您使用安全的基础权限。
							</li>
							<li>
								<strong>读取、写入存储卡</strong>
								：我们会在安卓App运行时询问您是否同意我们读取、写入您存储卡，用于实时保存您在{{ APP_DISPLAY_NAME }}中下载的模板及素材。如您拒绝该权限，我们将无法保存您下载的模板及素材。
							</li>
							<li>
								<strong>查看WLAN连接</strong>
								：我们还会在安卓App中调用该权限以便查看您的网络状态，以便在网络发生中断时提醒您。
							</li>
							<li>
								<strong>使用加速度传感器</strong>
								：我们还会在安卓App中调用加速度传感器来检测设备的摇动事件，以便在某些特定H5设计模板中提供摇动动效或者其他独特的动画效果。
							</li>
						</ul>
					</li>
				</ul>

				<h3 class="text-lg font-medium text-gray-800 mb-2 mt-4">1.4 我们从第三方获取的您的个人信息</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					我们可能会在您的同意范围内从第三方（我们的合作方）处收集并使用您的个人信息。我们保证依照与第三方签订的合同以及相关法律法规的规定处理您的个人信息，同时请您详细阅读该第三方的隐私政策及使用条款。如您拒绝第三方在提供服务时收集、使用您的个人信息，将可能导致您无法使用部分{{ APP_DISPLAY_NAME }}产品或服务。
				</p>

				<h4 class="text-base font-medium text-gray-800 mb-2">1.4.1 第三方跳转登录</h4>
				<p class="text-gray-700 leading-relaxed mb-4">
					当您使用经授权的下述第三方账号跳转登录时，我们会从第三方获取并共享与您账号相关的个人信息，并在您同意本政策后将该第三方账号与{{ APP_DISPLAY_NAME }}账号进行绑定。获取您授权的第三方账号共享的个人信息是为了记住您作为{{ APP_DISPLAY_NAME }}用户的登录身份，以便向您提供更优质的产品或服务，并且我们仅会在您同意的范围内使用此类信息。
				</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						当您选择使用「QQ账号」登录时，我们会收集从QQ账号共享的您注册使用的
						<strong><em>昵称、头像、地区、性别、Open ID</em></strong>
						及您在授权登录界面同意授予的信息。
					</li>
					<li>
						当您选择使用「微信账号」登录时，我们会收集微信账号共享的您注册使用的
						<strong><em>昵称、头像、地区、性别、Open ID</em></strong>
						及您在授权登录界面同意授予的信息。
					</li>
					<li>
						当您选择使用「微博账号」登录时，我们会收集微博账户共享的您注册使用的
						<strong><em>用户UID、昵称、头像</em></strong>
						及您在授权登录界面同意授予的信息。
					</li>
					<li>
						请您注意：我们提供{{ APP_DISPLAY_NAME }}账号与上述一个或者多个第三方账号同时绑定的功能，您可以进入{{ APP_DISPLAY_NAME }}应用"我的-设置-帐号与安全"中管理您的{{ APP_DISPLAY_NAME }}账号与第三方账号的授权解绑。如果您对操作有疑问，按照本政策第8条提供的方式联系我们。
					</li>
				</ul>

				<h4 class="text-base font-medium text-gray-800 mb-2 mt-4">1.4.2 其他间接收集</h4>
				<p class="text-gray-700 leading-relaxed">
					当您选择在支付场景登录第三方支付账号（如微信支付、支付宝支付、苹果支付）时，我们会收集第三方支付账户共享的您的
					<strong><em>头像、昵称、支付状态信息</em></strong>
					。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2 mt-4">1.5 征得同意的例外</h3>
				<p class="text-gray-700 leading-relaxed mb-4">根据法律法规，以下情形中收集和使用您的个人信息无需征得您的同意：</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>与履行法律法规规定的义务相关的；</li>
					<li>与国家安全、国防安全直接有关的；</li>
					<li>与公共安全、公共卫生、重大公共利益有关的；</li>
					<li>与犯罪侦查、起诉、审判和判决执行等有关的；</li>
					<li>出于维护您或其他用户的生命、财产等重大合法权益但又很难得到本人同意的；</li>
					<li>所涉及的个人信息是您自行向社会公众公开的；</li>
					<li>从合法公开披露的信息中收集个人信息的，如合法的新闻报道、政府信息公开等渠道；</li>
					<li>根据您的要求签订和履行合同所必需的；</li>
					<li>维护所提供的{{ APP_DISPLAY_NAME }}产品或服务的安全稳定运行所必需的，例如发现、处置产品或服务的故障；</li>
					<li>法律法规及监管政策规定的其他情形。</li>
				</ul>
			</section>

			<!-- 2. 如何使用Cookie -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">2. 如何使用Cookie</h2>
				<p class="text-gray-700 leading-relaxed mb-4">
					为确保{{ APP_DISPLAY_NAME }}应用正常运转，我们可能会将通过追踪技术收集所得的信息与我们收集所得关于您的其他信息合并。我们在为您提供服务时主要使用Cookie这一追踪技术，Cookie是您登录网站或浏览网络内容时，网站服务器存放在您的电脑、移动电话或其他智能终端设备内的小型文件，通常包含标识符、站点名称以及一些号码和字符。当您再次访问相应网站时，网站就可通过
					Cookie识别您的浏览器。Cookie可能会存储用户偏好及其他信息。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">我们一般会在以下场景使用Cookie技术：</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<strong>识别用户</strong>
						：当您使用账号登录{{ APP_DISPLAY_NAME }}应用时，借助于Cookie，我们可以在您每次使用我们的服务时识别您是否属于我们的用户。
					</li>
					<li>
						<strong>偏好设置</strong>
						：借助于Cookie，我们能够保存您之前的设置，例如移动设备的语言、输入设置或输入习惯等内容。这意味着，您无需在每次访问时重新配置您的偏好。
					</li>
					<li>
						<strong>优化用户体验</strong>
						：如果您在使用{{ APP_DISPLAY_NAME }}应用时遇到一些错误或漏洞，我们会通过Cookie进行记录，以便我们及时修复。
					</li>
				</ul>
				<p class="text-gray-700 leading-relaxed mb-4">除 Cookie外，我们还会使用网站信标技术。我们主要在以下情形中使用此类技术：</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<strong>统计访客</strong>
						：借助于网络信标，我们可以计算浏览网页的用户数量，并通过访问Cookie以辨认您是否属于我们的用户。
					</li>
					<li>
						<strong>偏好设置</strong>
						：我们向您发送的电子邮件可能含有链接至我们网站内容的点击URL。如果您点击该链接，我们会跟踪此次点击，帮助我们了解您的产品和服务的偏好并改善客户服务。如果您不希望自己的活动以这种方式被追踪，则可以随时从我们的寄信名单中选择退订。
					</li>
					<li>
						<strong>是否打开邮件</strong>
						：在某些情况下，我们可能会向您发送电子邮件，借助于网络信标，我们可以获知发送给您的邮件是否已经被打开。
					</li>
				</ul>
				<p class="text-gray-700 leading-relaxed">
					您可以通过在系统清除缓存的方式删除通过追踪技术已stored在本地的缓存数据，也可以通过手机本身的设置撤回对该App使用存储权限的授权来拒绝本地数据缓存。
				</p>
				<p class="text-gray-700 leading-relaxed">
					提示您注意并理解，倘若您拒绝使用追踪技术，则可能需要在每一次访问{{ APP_DISPLAY_NAME }}应用时亲自更改相关用户设置，并且，我们可能无法向您提供更加优质的使用体验，部分服务亦可能会无法正常运作。
				</p>
			</section>

			<!-- 3. 如何委托处理、共享、转让及披露您的个人信息 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">3. 如何委托处理、共享、转让及披露您的个人信息</h2>

				<h3 class="text-lg font-medium text-gray-800 mb-2">3.1 委托处理</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					为了向您提供更完善、更优质的产品和服务，某些功能可能由我们的技术服务合作方提供，我们可能会委托合作方处理您的某些个人信息。对我们委托处理您个人信息的公司、组织和个人，我们会与其签署严格的保密协定，明确数据处理事项、处理期限、处理性质、处理目的以及双方责任等，要求合作方仅按照我们的要求、本政策的规定以及其他任何相关的保密和安全措施来处理您的个人信息。我们会委托提供技术服务的第三方SDK提供商处理您的个人信息。关于提供商的身份、收集个人信息的目的、方式、范围等情况，请您详细参见本政策附录"第三方合作伙伴收集个人信息情况列表"。如果您拒绝我们的供应商在提供服务时收集为提供服务所必需的个人信息，将可能导致您在使用{{ APP_DISPLAY_NAME }}产品或服务的过程中无法享受该供应商提供的服务。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">3.2 共享</h3>
				<p class="text-gray-700 leading-relaxed mb-4">除非存在以下一种或多种情形，否则我们不会与任何第三方公司、组织和个人共享您的个人信息：</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>基于您自行提出的要求、或事先获得您的明示授权或同意；</li>
					<li>与我们履行法律法规规定的义务相关的；</li>
					<li>与国家安全、国防安全直接相关的；</li>
					<li>与公共安全、公共卫生、重大公共利益直接相关的；</li>
					<li>与犯罪侦查、起诉、审判和判决执行等直接相关的；</li>
					<li>出于维护您或其他个人的生命、财产等重大合法权益但又很难得到本人同意的；</li>
					<li>您自行向社会公众公开的您的个人信息；</li>
					<li>从合法公开披露的信息中收集个人信息的，如通过合法的新闻报道、政府信息公开等渠道。</li>
					<li>
						<strong>关联公司间共享</strong>
						：为便于我们基于{{ APP_DISPLAY_NAME }}关联账号共同向您提供服务，保护提供{{ APP_DISPLAY_NAME }}应用或服务的关联公司或其他用户或公众的人身财产安全免遭侵害，您的个人信息可能会在我们的关联公司间进行共享。我们只会共享您必要的个人信息，且这种共享受本政策所声明目的的约束。一旦我们共享您的个人信息或关联公司改变个人信息的处理目的，我们将会再次征求您的授权同意。共享个人信息的范围将根据具体业务情况而定，如为方便您通过{{ APP_DISPLAY_NAME }}账号使用由我们关联公司提供的{{ APP_DISPLAY_NAME }}其他产品或服务，我们会共享您的
						<strong><em>个人注册账号信息</em></strong>
						，以及根据本政策第1.1.2条描述共享您的
						<strong><em>画像信息</em></strong>
						。
					</li>
					<li>
						<strong>与合作方共享</strong>
						：为了向您提供更完善、优质的产品和服务，我们的某些服务将由合作方提供。我们可能会与合作方共享您的某些个人信息，以提供更好的客户服务和用户体验。我们仅会出于合法、正当、必要、特定、明确的目的共享您的个人信息，并且只会共享提供服务所必要的个人信息。我们会对合作方获取信息的软件工具包（SDK）、应用程序接口（API）进行严格的安全监测，以保护数据安全。同时，我们会与合作方签署严格的保密协定，要求他们严格按照我们的说明、本政策以及其他任何相关的保密和安全措施来处理您的个人信息。我们将要求我们的合作方不得将共享的个人信息用于任何其他用途。
					</li>
				</ul>
				<p class="text-gray-700 leading-relaxed mb-4">
					请您知悉：该等共享的合作方为数据控制者，以其自己的名义获得您的同意以处理您的个人信息。合作方可能有其独立的隐私政策和用户协议，我们建议您认真阅读并遵守第三方的用户协议及隐私政策。如果您拒绝我们的合作方在提供服务时收集为提供服务所必需的个人信息，将可能导致您无法在{{ APP_DISPLAY_NAME }}应用中享用该合作方提供的服务。您可以通过附件《{{ APP_DISPLAY_NAME }}应用内嵌第三方SDK目录》，详细了解{{ APP_DISPLAY_NAME }}应用接入的第三方SDK名称、收集的信息类型和用途等信息。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">目前，我们的合作方包括以下类型：</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<strong>第三方SDK合作伙伴</strong>
						：我们会通过应用程序接口(API)、软件工具开发包（SDK）与第三方进行必要的数据共享。不同版本的第三方SDK会有所不同，一般包括第三方账号登录类、第三方支付类、统计类等，我们会对第三方进行严格的安全检测并约定数据保护措施。关于提供商的身份、收集个人信息的目的、方式、范围、以及第三方的隐私声明，请您详细参见本政策附录"第三方合作伙伴收集个人信息情况列表"。
					</li>
					<li>
						<strong>数据分析、信息推广合作伙伴</strong>
						：我们可能会与该类合作伙伴共享您的
						<strong><em>位置信息、IP地址、Cookie数据、设备信息</em></strong>
						或经过加密后的标签信息，以用于监察投放的情况、为用户提供更符合需求的分析结果、实现推广信息的有效触达、优化推广效果。
					</li>
					<li>
						<strong>提供客服、用户管理、物流和其他服务的供应商、服务提供商和其他合作方</strong>
						：我们可能会将您的个人信息共享给支持我们提供服务的第三方。这些机构包括为我们提供客户服务、用户管理、物流服务等的机构。例如，我们为您寄送实物产品时，我们会向您提供物流发货、订单查询、售后服务、客户支持等服务，我们可能将您的
						<strong><em>姓名、电话号码、住址</em></strong>
						共享给服务提供商。
					</li>
				</ul>
				<p class="text-gray-700 leading-relaxed mb-4">我们要求这些合作方只能出于为我们或您提供服务的目的使用您的信息，这些信息的用途受到如下限制：</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>协助{{ APP_DISPLAY_NAME }}向您提供服务；</li>
					<li>协助{{ APP_DISPLAY_NAME }}履行在上文所述「向您提供产品和/或服务」第1.1条及第1.2条所载的目的；</li>
					<li>履行与{{ APP_DISPLAY_NAME }}根据已签订的协议、本政策的责任和实现{{ APP_DISPLAY_NAME }}的权利及/或协助理解和改善{{ APP_DISPLAY_NAME }}提供的服务。</li>
				</ul>
				<p class="text-gray-700 leading-relaxed mb-4">
					请您知悉：随着{{ APP_DISPLAY_NAME }}产品或服务的更新和升级，您可以通过使用{{ APP_DISPLAY_NAME }}产品或服务或第三方提供的功能，主动公开、共享您的模板及素材（可能会含有您的个人信息）到第三方平台（如微信好友/朋友圈/QQ/微博/抖音等），还可以通过发送电子邮件、共享二维码或链接的方式将您的模板及素材（可能会含有您的个人信息）发送给特定或不特定的第三方。在这种情况下，共享信息将被即时并广泛的传播。一旦您进行了此类信息的共享，很难确保当您需要删除信息时，您所共享的信息在所有接受转发的互联网平台中被全部删除。在一些情况下，即使您从{{ APP_DISPLAY_NAME }}产品或服务中删除了您共享的信息，该类信息仍然可能会留存在公众可获知的领域，仍可由其他用户或与我们没有关联且不受我们控制的第三方独立地缓存、复制或存储，以及通过其他用户或该等第三方在公众可获知的领域保存。但提醒您注意，您未主动分享的内容是不会被公众所访问的。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">3.3 转让</h3>
				<p class="text-gray-700 leading-relaxed mb-4">原则上，我们不会将您的个人信息控制权向其他公司、组织和个人转让，但以下情况除外：</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>基于您自行提出的要求、或事先获得您明确的同意或授权；</li>
					<li>与我们履行法律法规规定的义务相关的；</li>
					<li>与国家安全、国防安全直接相关的；</li>
					<li>与公共安全、公共卫生、重大公共利益直接相关的；</li>
					<li>与犯罪侦查、起诉、审判和判决执行等直接相关的；</li>
					<li>出于维护您或其他个人的生命、财产等重大合法权益但又很难得到本人同意的；</li>
					<li>您自行向社会公众公开的您个人信息；</li>
					<li>从合法公开披露的信息中收集个人信息的，如通过合法的新闻报道、政府信息公开等渠道；</li>
					<li>发生公司合并、收购或破产清算，运营主体变更时的转让。</li>
				</ul>
				<p class="text-gray-700 leading-relaxed">
					请您知悉：如具备上述事由确需转让，我们将在转让前向您告知转让信息的目的、类型、信息受让方（如涉及敏感信息，我们还将告知涉及的敏感信息的内容），并在征得您同意后再转让，但法律法规另有规定的除外。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2 mt-4">3.4 公开披露</h3>
				<p class="text-gray-700 leading-relaxed">原则上我们不会向社会或不特定人群发布您的个人信息，但以下情况除外：</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>基于与您的约定的；</li>
					<li>根据法律法规应当披露的。</li>
				</ul>
			</section>

			<!-- 4. 您如何管理您的个人信息 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">4. 您如何管理您的个人信息</h2>

				<h3 class="text-lg font-medium text-gray-800 mb-2">4.1 访问或更正您的个人信息</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					您有权随时登录并访问您的{{ APP_DISPLAY_NAME }}账号，查看您的个人信息，包括您的账号信息、个人资料、支付信息、密码、绑定邮箱和工作描述。如您发现我们收集、存储、使用、披露的您的个人信息有错误的，或者在法律法规规定的其他情况下，您可以更正您的个人信息。
				</p>
				<p class="text-gray-700 leading-relaxed">
					如您希望访问或更正您的其他个人信息，或在行使上述权利过程中遇到困难，您可通过本政策第8条提供的联系方式联系我们，我们将在核实您的身份后【15个工作日内】响应您的请求，但法律法规另有规定的或本政策另有约定的除外。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2 mt-4">4.2 删除您的个人信息</h3>
				<p class="text-gray-700 leading-relaxed mb-4">在以下情形中，您可以向我们提出删除您个人信息的请求，但已进行个人信息匿名化处理或法律法规另有规定的除外：</p>
				<ol class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>我们违反法律、行政法规的规定或与您的约定，收集、存储、使用、转移、披露您的个人信息的；</li>
					<li>我们超出目的范围或者必要期限收集、存储、使用、转移、披露您的个人信息的；</li>
					<li>您通过注销等方式终止使用{{ APP_DISPLAY_NAME }}产品或服务的；</li>
					<li>法律法规等规定的其他情形。</li>
				</ol>
				<p class="text-gray-700 leading-relaxed mb-4">
					如您希望删除您的其他个人信息，或在行使上述权利过程中遇到困难，您可通过本政策第8条提供的联系方式联系我们，我们将在核实您的身份后【15个工作日内】响应您的请求，但法律法规另有规定的或本政策另有约定的除外。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">
					<b>您在此同意，一旦删除请求被响应，您的个人信息将被删除或匿名化处理，</b>
					除非法律法规或规范性文件另有规定。
				</p>
				<h3 class="text-gray-800 font-semibold leading-relaxed mb-4">请您知悉，我们可能不会将响应的信息立即从备份系统中删除，但会在备份系统更新时进行删除。</h3>

				<h3 class="text-lg font-medium text-gray-800 mb-2 mt-4">4.3 更改您授权同意的范围</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					请您知悉，为了您能正常使用{{ APP_DISPLAY_NAME }}产品或服务，您需要向我们开启设备中的相关权限和/或提供基本的个人信息。如果您希望关闭上文第1.3条中的设备权限，您可以通过您设备中的设置页面进行操作。如果您解除与第三方账号的绑定，您可以通过"我的-设置-帐号与安全"的设置页面进行相应操作。如果您希望撤回您先前对于{{ APP_DISPLAY_NAME }}产品或服务内相应功能的授权，您可以通过相关的服务页面进行相应操作。例如：
				</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						禁止{{ APP_DISPLAY_NAME }}访问您的相册或摄像头：您可进入移动设备的"设置"界面，在应用的权限管理或隐私项目中找到{{ APP_DISPLAY_NAME }}，并修改{{ APP_DISPLAY_NAME }}的权限以撤回您允许{{ APP_DISPLAY_NAME }}访问您相册和摄像头的授权。
					</li>
					<li>
						禁止{{ APP_DISPLAY_NAME }}访问您的麦克风：您可进入移动设备的"设置"页面，在应用的权限管理或隐私项目中找到{{ APP_DISPLAY_NAME }}，并修改{{ APP_DISPLAY_NAME }}的权限以撤回您允许{{ APP_DISPLAY_NAME }}访问您麦克风的授权。
					</li>
					<li>断开在注册/登录时关联的第三方社交平台账号：您可以在{{ APP_DISPLAY_NAME }}app或网页端账户的"设置"页面，在"你的账户"中选择断开与微信、QQ、微博及其他社交平台的关联。</li>
				</ul>
				<p class="text-gray-700 leading-relaxed">
					当您更改您的同意范围后，我们将不再收集和使用您相应的个人信息，但请您理解，在您更改您的同意范围后，我们无法再为您提供与之对应的{{ APP_DISPLAY_NAME }}产品或服务。
					<b>您在此同意，您更改授权同意的范围或撤回授权同意的行为不会影响我们基于您之前的授权同意对您个人信息进行的处理。</b>
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2 mt-4">4.4 注销您的账号</h3>
				<p class="text-gray-700 leading-relaxed mb-4">您可以根据自己的选择随时向我们提出注销您已注册的{{ APP_DISPLAY_NAME }}账号的请求。</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>您可以通过以下路径在APP内自行提交注销申请：【我的】【设置】【其他设置】【账户注销】，并根据页面提示完成注销流程。</li>
					<li>如您在操作过程中遇到任何问题，或希望通过其他方式注销，也可以通过在我们{{ APP_DISPLAY_NAME }}平台产品中任意客户服务入口与我们取得联系协助您注销账户。</li>
				</ul>
				<p class="text-gray-700 leading-relaxed mt-4 mb-4">我们会在15个工作日内核实您的身份信息后为您注销账号。</p>
				<p class="text-gray-700 leading-relaxed mb-4">
					删除账户后，{{ APP_DISPLAY_NAME }}将自动退出登录且您无法继续使用原有的用户名和密码访问您的{{ APP_DISPLAY_NAME }}账户。如您希望恢复您的账户，您可在主动删除账户之日起10个自然日内通过本政策第8条提供的方式联系{{ APP_DISPLAY_NAME }}客服要求恢复您对被删除账户的访问权限并继续正常使用。如您未在上述期限申请恢复的，您的账户及账户内的模板及素材将被彻底删除。请您知悉，在注销账号后，除非根据法律法规或根据监管部门的要求保留或存储您的个人信息，否则我们将及时删除您的个人信息或作匿名化处理。
				</p>
				<p class="text-gray-700 leading-relaxed">
					您注销账号的行为是不可逆的，一旦您的账号被注销，我们将不再为您提供{{ APP_DISPLAY_NAME }}产品或服务，也不再收集您的个人信息。我们建议您谨慎选择注销账号。
				</p>
			</section>

			<!-- 5. 如何存储您的个人信息 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">5. 如何存储您的个人信息</h2>
				<p class="text-gray-700 leading-relaxed mb-4">
					原则上，我们会将在中国境内运营所收集和产生的个人信息存储在中国境内，不主动将其向境外转移。如您主动向与我们合作的境外合作伙伴分享您的个人信息的，请在分享前仔细阅读该合作伙伴的隐私保护政策。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">
					我们承诺，除非法律法规另有要求，我们对您的个人信息的存储时间将限于实现您授权使用的目的所必需的最短时间，除非法律法规或规范性文件另有规定。
				</p>
				<p class="text-gray-700 leading-relaxed">
					如我们因业务需求或其他原因出现停止运营的情况，除非法律法规或规范性文件另有规定，我们将会向您告知此类情况，并停止对您个人信息进行收集，同时删除已收集到的个人信息或对其做匿名化处理。
				</p>
			</section>

			<!-- 6. 如何保护未成年人个人信息 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">6. 如何保护未成年人个人信息</h2>
				<p class="text-gray-700 leading-relaxed mb-4">我们根据我国法律法规的要求，保护未成年人的个人信息安全。如果您是：</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<strong>儿童</strong>
						（详情请查阅关键词定义）使用者，必须由您的监护人阅读并同意本协议的全部内容后，我们方才向您提供{{ APP_DISPLAY_NAME }}产品或服务；
					</li>
					<li>
						<strong>已满14周岁的未成年人</strong>
						使用者，必须由您的监护人陪同阅读本协议的全部内容，并在监护人或您本人同意本协议的全部内容后，我们才向您提供{{ APP_DISPLAY_NAME }}产品或服务。
					</li>
				</ul>
				<p class="text-gray-700 leading-relaxed mb-4">如我们有证据证明您违反了上述约定，我们有权停止提供我们的产品或服务。</p>
				<p class="text-gray-700 leading-relaxed">
					对于经监护人同意而收集未成年人个人信息的情况，我们只会在法律允许、监护人明确同意或者保护未成年人所必要的情况下使用或公开披露该信息。如我们发现在未事先获得监护人同意的情况下收集了被其监护的未成年人的个人信息，我们会尽快删除相关信息。
				</p>
				<p class="text-gray-700 leading-relaxed">
					有关未成年人隐私保护的更多细节，请参阅我们的
					<a href="/children.html" class="text-blue-600 hover:underline">《儿童个人信息保护政策》</a>
					。
				</p>
			</section>

			<!-- 7. 文案生成、创作、搜索 -->
			<!-- 7. 文案生成、创作、搜索 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">7. 文案生成、创作、搜索</h2>

				<h3 class="text-lg font-medium text-gray-800 mb-2">7.1 技术原理说明</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					我们基于生成式人工智能模型技术向您提供文案生成、创作、搜索服务。您可以通过
					<a href="/algorithm.html" class="text-blue-600 hover:underline">《算法及模型备案公示说明》</a>
					查看和详细了解我们使用的模型技术的基本原理、目的意图和主要运行机制等。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">7.2 必要权限申请与使用</h3>
				<p class="mb-4">
					对于更改头像、上传照片、上传参考文件时，我们会向您申请相册、存储权限，只有经过您的明示授权才会在为实现特定功能或服务时使用，您也可以撤回授权。特别需要指出的是，即使经过您的授权，我们获得了这些敏感权限，也不会在相关功能或服务不需要时收集您的信息。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">7.3 服务所需信息收集</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					为了向您提供文案生成、搜索、创作服务，我们可能会收集您主动输入的信息（包括文字、文件、以及您手写输入的信息）、内容指令，您在使用本产品时的行为信息（包括您的点击、浏览、编辑等操作记录），以及您提供的反馈信息（跳转商店评论、提交反馈）。我们建议在您拍摄照片，以及输入文字信息和音频信息时应采取积极措施保护个人信息和个人隐私的安全，包括但不限于：避免输入个人信息或隐私、避免将个人信息或隐私拍入照片。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">7.4 增值服务与使用分析</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					为了向您提供文案生成、搜索、创作功能使用情况的分析增值服务，我们可能会收集您的头像、昵称、手写输入的信息、使用情况、使用数据、准确率，并对相关信息进行分析处理后向您展示使用情况分析报告、提供使用建议或类似的增值服务。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">7.5 历史信息收集</h3>
				<p class="text-gray-700 leading-relaxed mb-4">为便于提升服务效果，我们将收集您文案生成、搜索、创作的历史信息。</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">7.6 信息分析与模型优化</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					我们会对上述信息进行分析与计算，以便于更好地理解您的问题和上下文语境，从而为您提供更具有相关性的信息。同时，在经安全加密技术处理、严格去标识化且无法重新识别特定个人的前提下，我们可能会将上述所收集的数据以及模型生成的对应回复用于模型训练，不断调整优化模型的效果。如果您不希望您输入或提供的信息用于模型和优化，可以通过本隐私政策第10条（如何联系我们）与我们联系，要求撤回使用您的数据用于模型训练和优化。
				</p>
			</section>

			<!-- 8. 运营与安全运行/保障 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">8. 运营与安全运行/保障</h2>
				<p class="text-gray-700 leading-relaxed">
					我们致力于为您提供安全、可信的产品与使用环境，提供优质而可靠的服务与信息是我们的核心目标。为了维护我们服务的正常运行，保护您或其他用户或公众的合法利益免受损失，我们会收集用于维护产品或服务安全稳定运行的必要信息。
				</p>
			</section>

			<!-- 9. 如何更新本政策 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">9. 如何更新本政策</h2>
				<p class="text-gray-700 leading-relaxed mb-4">
					我们保留不时更新或修改本政策的权利。但是未经您明确同意，我们不会削减您按照本政策所应享有的权利。该等变更构成本政策的一部分。我们会在本产品中发布更新后的本政策内容。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">
					请您仔细阅读变更后的本政策内容。如果您不同意本政策，或对本政策修改、更新的内容有异议，您可以选择停止使用{{ APP_DISPLAY_NAME }}产品或服务或者注销{{ APP_DISPLAY_NAME }}账号。但请您知悉，您账号注销之前、停止使用本服务之前的行为和活动仍受本政策的约束。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">若涉及重大、实质性变更，我们会依据具体情况，以显著的方式通知您。</p>
				<p class="text-gray-700 leading-relaxed mb-4">重大、实质性变更的情形包括但不限于以下情形：</p>
				<ul class="list-decimal pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>我们的服务模式发生重大变化。如处理个人信息的目的、处理的个人信息类型、个人信息的使用方式等；</li>
					<li>我们在所有权结构、组织架构等方面发生重大变化。如业务调整、破产并购等引起的所有者变更等；</li>
					<li>个人信息共享、转让或公开披露的主要对象发生变化；</li>
					<li>您参与个人信息处理方面的权利及其行使方式发生重大变化；</li>
					<li>我们负责处理个人信息安全的责任部门、联络方式及投诉渠道发生变化时；</li>
					<li>以及个人信息安全影响评估报告表明存在高风险时。</li>
				</ul>
			</section>

			<!-- 10. 如何联系我们 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">10. 如何联系我们</h2>
				<p class="text-gray-700 leading-relaxed mb-4">如您在使用{{ APP_DISPLAY_NAME }}时对本隐私政策有任何问题，您可通过地址、电话和电子邮箱，以书面信函形式或电子邮件方式联系我们。</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<strong>地址</strong>
						：{{ CONTACT_ADDRESS }}
					</li>
					<li>
						<strong>电话</strong>
						：{{ CONTACT_PHONE }}
					</li>
					<li>
						<strong>电子邮箱</strong>
						：
						<a :href="'mailto:' + CONTACT_EMAIL" class="text-blue-600 hover:underline">{{ CONTACT_EMAIL }}</a>
					</li>
				</ul>
				<p class="text-gray-700 leading-relaxed mb-4">对于您的联系与反馈，我们将尽快审核所涉问题，在收到您请求后【15个工作日内】响应您的请求，给予回复。</p>
				<p class="text-gray-700 leading-relaxed">
					您对我们的回复不满意，或我们对您个人信息的处理方式侵害了您的合法权益，且在您向我们反馈后我们无法提供让您满意的回复且拒绝改进的，您可以向相关监管机构进行投诉和举报。
				</p>
			</section>

			<!-- 11. 免责声明 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">11. 免责声明</h2>

				<h3 class="text-lg font-medium text-gray-800 mb-2">11.1 用户内容合规保证</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					使用本服务时请您确保您所输入的内容未侵害他人权益，未涉及不良信息及情色内容，同时未输入与政治相关的内容，且所有输入内容均合法合规。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">11.2 违规使用处理</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					如果平台注意到您有意输入特定内容（包括但不限于11.1范围），以此来诱导促使AI生成不良内容并进行传播造谣，对我们的公司造成不良影响的，我们将保留对您账户实施永久禁止使用的权利，并按照法律规定追究您的法律责任。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">11.3 生成内容免责</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					您确认并知悉当前服务生成的所有内容均由人工智能模型生成，生成内容的准确性、完整性、功能性均会受到当前的技术机制限制。本平台对其生成内容的准确性、完整性和功能性不做任何保证，并且其生成的内容均不代表我们的态度或观点。AI模型发出的信息是由计算机生成，可能是虚构的，需要您自行辨别是非。您在使用本应用咨询医疗、法律等相关专业领域问题时，本应用仅提供医疗建议参考和法律建议参考，不能代表专业的医疗服务、法律服务或治疗方案方法。您在使用本应用前，必须咨询医生和相关法律等专业人士，对医疗或法律决定负最终责任。本平台应用提供的信息和服务仅作为参考之用，不代表任何医疗、法律建议或治疗方法。对于使用本应用可能引起的任何责任，我们不予承担。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">11.4 信息内容来源与反馈</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					我们的服务严格遵循法律法规，数据来源包括公开互联网等合法渠道。我们已通过自动化与人工结合的方式对敏感信息进行过滤，但仍无法完全保证所有内容的准确性、合理性或符合您的预期。如您发现任何问题或感到不适，欢迎通过平台邮箱（
					<a :href="'mailto:' + CONTACT_EMAIL" class="text-blue-600 hover:underline">{{ CONTACT_EMAIL }}</a>
					）向我们反馈。我们高度重视您的意见，并将及时采取有效措施处理。感谢您的理解与支持。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">11.5 用户协议指引</h3>
				<p class="text-gray-700 leading-relaxed mb-4">
					您在同意接受本声明之前，具体的使用规范请严格按照《
					<a href="/agreement.html" class="text-blue-600 hover:underline">用户协议</a>
					》的内容进行输入。
				</p>

				<h3 class="text-lg font-medium text-gray-800 mb-2">11.6 声明生效与接受</h3>
				<p class="text-gray-700 leading-relaxed">
					无论您是否已实际阅读本声明，当您通过网络页面点击确认同意本声明或实际使用本平台（"我方"）提供的本服务时，均表示您与我方已达成协议并接受所有在本声明中所述的条款与条件。这意味着您同意遵守本平台的使用规定，包括但不限于您对信息的使用、发布内容的规范、以及我们提供服务的方式。我们鼓励您在使用本平台提供的服务前，仔细阅读并充分理解本声明的各项内容。
				</p>
			</section>

			<!-- 12. 附则 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">12. 附则</h2>
				<p class="text-gray-700 leading-relaxed mb-4">
					本政策的解释及争议解决均应适用中华人民共和国法律。与本政策相关的任何纠纷，双方应经友好协商解决；若不能协商解决，您在此同意将争议提交至中国广州仲裁委员会裁决。仲裁裁决是终局的，对双方均有约束力。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">
					如果有管辖权的仲裁机构裁决本政策的任何条款无效，则该条款将从本政策中移除，但该条款的无效不影响本政策其余条款的效力。本政策的其余条款将继续执行。
				</p>
				<p class="text-gray-700 leading-relaxed mb-4">本政策的标题仅为方便阅读而设计，不影响本政策任何条款的含义或解释。</p>
				<p class="text-gray-700 leading-relaxed mb-4">
					有关{{ APP_DISPLAY_NAME }}接入的第三方SDK收集和使用用户信息的具体情况，请参阅《
					<a href="/thirdSDK.html" class="text-blue-600 hover:underline">第三方SDK目录</a>
					》。
				</p>
			</section>
		</div>
	</div>
</template>

<script setup lang="ts">
import { APP_DISPLAY_NAME, COMPANY_NAME, CONTACT_PHONE, CONTACT_EMAIL, CONTACT_ADDRESS, OFFICIAL_WEBSITE } from '@/config/app'
</script>
